import Arrays from '../../generic/utility/Arrays';
import PolyMaps from '../model/PolyMaps';
import ScalarBuffer from '../../generic/container/ScalarBuffer';
import BitSet from '../../generic/container/BitSet';
import IndexMappings from '../../generic/algorithm/IndexMappings';

export default function Selection(indices, meaning, optionalMapId) {

  this.indices = indices;
  this.meaning = meaning;
  this.mapId = optionalMapId || PolyMaps.IdPositions;
};

Selection.Faces = 0;
Selection.Vertices = 1;
Selection.Edges = 2;

Selection.prototype = {

  constructor: Selection,

  getIndices: function (mesh, meaning, optionalMapId) {

    var indices = this.indices,
      convTab = Selection.ConversionTable,
      convFunc = convTab[this.meaning][meaning];

    if (convFunc !== null)

      indices = convFunc.call(null, mesh, indices,
          this.mapId, optionalMapId || PolyMaps.IdPositions);

    return indices;
  },

};

Selection.newRandomSelection =
    function (seed, density, polyMesh, meaning, optionalMapId) {

  var n = Selection.getNumElements(polyMesh, meaning, optionalMapId),

    nPopulation = density * n | 0,
    bits = new BitSet(n).fill(0, nPopulation),
    prng = seed;

  for (var i = 0, e = n - 1; i < e; ++i) {

    // Magic constants taken from
    // https://en.wikipedia.org/wiki/Linear_congruential_generator
    prng = prng * 22695477 + 1 >>> 0;

    bits.swap(i, i + (prng & 0x3fffffff) % (n - i));
  }

  return new Selection(bits.toIndexArray(), meaning, optionalMapId);

};


// Provides an identity mapping in case a selection is not provided:

Selection.getIndices = function (
    mesh, meaning, optionalSelectionOrMapId, optionalSelection) {

  var mapId = PolyMaps.IdPositions,
    selection = optionalSelection;

  if (typeof optionalSelectionOrMapId === 'string')
    mapId = optionalSelectionOrMapId;
  else
    selection = optionalSelectionOrMapId;

  return !selection ?

      IndexMappings.identityForReading(
        Selection.getNumElements(mesh, meaning, mapId)) :

      selection.getIndices(mesh, meaning, mapId);
};

Selection.getNumElements = function (mesh, meaning, optionalMapId) {

  switch (meaning) {

    case Selection.Faces:
      return mesh.getNumFaces();

    case Selection.Vertices:
      return PolyMaps.resolveMap(mesh,
          optionalMapId || PolyMaps.IdPositions).values.length;

    case Selection.Edges:
      return PolyMaps.resolveMap(mesh,
          optionalMapId || PolyMaps.IdPositions).getNumEdges();

    default:
      throw Error('Bad meaning argument!');
  }
};

// Conversions:

Selection.convFacesToVertices = function (mesh, indices, _, mapId) {

  var mapOut = PolyMaps.resolveMap(mesh, mapId),
    result = new BitSet(mapOut.values.length),

    faceRangeOffsets = mesh.faceRangeOffsets,
    mapValueIndices = mapOut.faceValueIndices;

  for (var i = 0, n = indices.length; i !== n; ++i) {

    var faceIndex = indices[i],

      begin = faceRangeOffsets[faceIndex],
      until = faceRangeOffsets[faceIndex + 1];

    result.includeFromArray(mapValueIndices, begin, until);
  }

  return result.toIndexArray();
};

Selection.convFacesToEdges = function (mesh, indices, _, mapId) {

  var mapOut = PolyMaps.resolveMap(mesh, mapId),
    result = new BitSet(mapOut.getNumEdges()),

    faceRangeOffsets = mesh.faceRangeOffsets,
    mapValueIndices = mapOut.faceValueIndices;

  for (var i = 0, n = indices.length; i !== n; ++i) {

    var faceIndex = indices[i],

      begin = faceRangeOffsets[faceIndex],
      until = faceRangeOffsets[faceIndex + 1];

    if (begin !== until) {

      var vertexA = mapValueIndices[until - 1];

      for (var j = begin; j !== until; ++j) {

        var vertexB = mapValueIndices[j];
        result.include(mapOut.findEdgeId(vertexA, vertexB));
        vertexA = vertexB;
      }
    }
  }

  return result.toIndexArray();
};

//

Selection.convVerticesToFaces = function (mesh, indices, mapId, _) {

  var buffer = new ScalarBuffer(Uint32Array),

    faceRangeOffsets = mesh.faceRangeOffsets,
    mapValueIndices =
        PolyMaps.resolveMap(mesh, mapId).faceValueIndices,

    nFaces = mesh.getNumFaces();

  for (var faceIndex = 0; faceIndex !== nFaces; ++faceIndex) {

    var begin = faceRangeOffsets[faceIndex],
      until = faceRangeOffsets[faceIndex + 1],

      allVerticesFound = true;

    for (var i = begin; i !== until && allVerticesFound; ++i)

      allVerticesFound = -1 <
          Arrays.binarySearch(indices, mapValueIndices[i]);

    if (allVerticesFound) buffer.push(faceIndex);
  }

  return buffer.toArrayAndClear();
};

Selection.convVerticesToVertices =
    function (mesh, indices, mapIdIn, mapIdOut) {

  if (mapIdIn === mapIdOut) return indices;

  var mapOut = PolyMaps.resolveMap(mesh, mapIdOut),
    result = new BitSet(mapOut.values.length),

    mapOutValueIndices = mapOut.faceValueIndices,

    mapIn = PolyMaps.resolveMap(mesh, mapIdIn).updateInverseIndex(),
    faceIndexOffsets = mapIn.valueFaceIndexOffsets,
    faceIndices = mapIn.valueFaceIndices;

  for (var i = 0, n = indices.length; i !== n; ++i) {

    var mapInValueIndex = indices[i],

      facesBegin = faceIndexOffsets[mapInValueIndex],
      facesUntil = faceIndexOffsets[mapInValueIndex + 1];

    for (var j = facesBegin; j !== facesUntil; ++j) {

      var vertexOffset =
          mapIn.findValueIndexOffset(
              faceIndices[j], mapInValueIndex);

      result.include(mapOutValueIndices[vertexOffset]);
    }
  }

  return result.toIndexArray();
};

Selection.convVerticesToEdges =
    function (mesh, indices, mapIdIn, mapIdOut) {

  var mapOut = PolyMaps.resolveMap(mesh, mapIdOut),
    result = new BitSet(mapOut.getNumEdges()),

    faceRangeOffsets = mesh.faceRangeOffsets,
    mapValueIndices =
        PolyMaps.resolveMap(mesh, mapIdIn).faceValueIndices,

    nFaces = mesh.getNumFaces();

  for (var faceIndex = 0; faceIndex !== nFaces; ++faceIndex) {

    var begin = faceRangeOffsets[faceIndex],
      until = faceRangeOffsets[faceIndex + 1];

    if (begin !== until) {

      var last = mapValueIndices[--until],
        lastSelected = -1 < Arrays.binarySearch(indices, last),

        vertexA = last,
        prevSelected = lastSelected;

      for (var j = begin; j !== until; ++j) {

        var vertexB = mapValueIndices[j],
          selected = -1 < Arrays.binarySearch(indices, vertexB);

        if (prevSelected && selected)

          result.include(mapOut.findEdgeId(vertexA, vertexB));

        vertexA = vertexB;
        prevSelected = selected;
      }

      if (prevSelected && lastSelected)
        result.include(mapOut.findEdgeId(vertexA, last));
    }
  }

  return result.toIndexArray();
};

//

Selection.convEdgesToFaces = function (mesh, indices, mapIdIn, _) {

  var tmp = Selection.
      convEdgesToVertices(mesh, indices, mapIdIn, mapIdIn);

  return Selection.convVerticesToFaces(mesh, tmp, mapIdIn, '');
};

Selection.convEdgesToVertices =
    function (mesh, indices, mapIdIn, mapIdOut) {

  var tmp = Selection.
      convEdgesToVerticesOfSameMap(mesh, indices, mapIdIn);

  return Selection.convVerticesToVertices(mesh, tmp, mapIdIn, mapIdOut);
};

Selection.convEdgesToEdges =
    function (mesh, indices, mapIdIn, mapIdOut) {

  if (mapIdIn === mapIdOut) return indices;

  var tmp = Selection.
      convEdgesToVerticesOfSameMap(mesh, indices, mapIdIn);

  return Selection.
      convVerticesToEdges(mesh, indices, mapIdIn, mapIdOut);
};

Selection.convEdgesToVerticesOfSameMap = function (mesh, indices, mapId) {

  var map = PolyMaps.resolveMap(mesh, mapId).updateEdgeMap(),

    edgeRangeOffsets = map.edgePivotRangeOffsets,
    highVertexIndices = map.edgeHighVertexIndices,

    result = new BitSet(map.values.length);

  for (var i = 0, n = indices.length; i !== n; ++i) {

    var edgeId = indices[i],

      hiVertex = highVertexIndices[edgeId],

      searchResult = Arrays.
          binarySearch(edgeRangeOffsets, edgeId + 1);

    if (searchResult < 0) searchResult = ~searchResult;

    var loVertex = searchResult - 1;

    result.include(hiVertex);
    result.include(loVertex);
  }

  return result.toIndexArray();
};

Selection.ConversionTable = [

  // From Faces to...
  [
    // ... Faces
    null,
    // ... Vertices
    Selection.convFacesToVertices,
    // ... Edges
    Selection.convFacesToEdges,
  ],
  // From Vertices to...
  [
    // ... Faces
    Selection.convVerticesToFaces,
    // ... Vertices
    Selection.convVerticesToVertices,
    // ... Edges
    Selection.convVerticesToEdges,
  ],
  // From Edges to...
  [
    // ... Faces
    Selection.convEdgesToFaces,
    // ... Vertices
    Selection.convEdgesToVertices,
    // ... Edges
    Selection.convEdgesToEdges,
  ],
];



// WEBPACK FOOTER //
// ./src/polyMesh/geometric/transient/Selection.js